import { useMemo, type ReactNode } from 'react';
import { RoomIcon } from '@/features/rooms/RoomIcon';
import { OriginLink as Link, useOriginNavigate } from '@/lib/origin';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { AlertTriangle, AtSign, IdCard, BarChart3, CalendarClock, CalendarDays, ClipboardCheck, Folder, GraduationCap, Hash, Heart, MessageSquare, Bell, Wrench } from 'lucide-react';
import { hrApprovalsTitle, hrDate, trainingDueLabel, type WidgetKey, type WidgetSize } from '@tradeoutlet/shared';
import { useHrApprovals } from '@tradeoutlet/data';
import { api } from '@/lib/api';
import { TZ } from '@/lib/env';
import { dt, minutes, shiftWhen, today, sentenceCase, clock } from '@/lib/format';
import { conversationName, useConversations, useMembers, useProjects, useWfContext, useWorkspaceId } from '@/lib/hooks';
import { qk } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { useAuth } from '@/lib/auth';
import { useModuleOn } from '@/lib/modules';
import { Avatar, Card, Spinner, Stat } from '@/components/ui';
import { PersonButton } from '@/features/people/PersonDrawer';
import { ClockWidget } from '@/features/workforce/ClockWidget';
import { HomeAnnouncements } from '@/features/knowledge/AnnouncementsPage';
import { PendingHandoversBanner } from '@/features/workforce/handovers/HandoversUi';
import { ShiftTimesLabel, useWfLookups } from '@/features/workforce/common';
import { useOpenLink } from '@/features/notifications/NotificationPanel';
import { BirthdaysWidget } from '@/features/people/BirthdaysWidget';
import { AckWidget } from '@/features/acks/AcksPage';
import { useCanAnywhere } from '@/lib/permissions';
import { chatHref, checklistsDueHref, hoursHref, isChecklistDue, isUnreadConversation, issuesHref, MY_OPEN_TASKS, myShiftsHref, statLabel, taskDueOf, tasksHref, type IssueQuery } from '@/lib/homeStats';
import { StatLink } from './StatLink';
import { ukToday } from '@/features/tasks/privateTasks';
import { MyTasksWidget } from '@/features/tasks/MyTasksWidget';
import { BasketWidget, TakingsWidget, TopSellersWidget, TransactionsWidget } from '@/features/sales/SalesWidgets';
import { RelativeTime } from '@/components/time/RelativeTime';

const T = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => <span className="flex items-center gap-2">{icon}{children}</span>;
const icon = 'h-4 w-4 text-accent-fg';
const Row = ({ children, onClick, to }: { children: ReactNode; onClick?: () => void; to?: string }) => {
  const cls = 'flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60';
  return to ? <Link to={to} className={cls}>{children}</Link> : <button onClick={onClick} className={cls}>{children}</button>;
};
const List = ({ children }: { children: ReactNode }) => <div className="-mx-4 -my-4 divide-y divide-slate-100 dark:divide-slate-800">{children}</div>;
const weekRange = () => {
  const d = DateTime.now().setZone(TZ).startOf('week');
  return { from: d.toISODate()!, to: d.plus({ days: 6 }).toISODate()! };
};

function useShifts() {
  const ws = useWorkspaceId();
  const wf = useWfContext();
  const shiftsOn = useModuleOn('shifts');
  const from = today();
  const to = DateTime.fromISO(from, { zone: TZ }).plus({ days: 14 }).toISODate()!;
  const q = useQuery({ queryKey: qk.wf(ws ?? '', 'my-shifts', from, to), queryFn: () => api.wf.myShifts(ws!, { from, to }), enabled: Boolean(ws && shiftsOn && wf.data?.is_employee) });
  return (q.data?.shifts ?? []).filter(s => s.status === 'published' && s.ends_at > new Date().toISOString());
}

function useUnread() {
  const convs = useConversations();
  return useMemo(() => (convs.data?.conversations ?? []).filter(isUnreadConversation), [convs.data]);
}

/** The Tasks list's default view (open, for me, all projects), so Home's counts match it row for row. */
function useMyOpenTasks() {
  const ws = useWorkspaceId();
  const tasksOn = useModuleOn('tasks');
  return useQuery({ queryKey: ['tasks-area', ws ?? '', MY_OPEN_TASKS], queryFn: () => api.workspaceTasks(ws!, MY_OPEN_TASKS), enabled: Boolean(ws && tasksOn), select: d => d.tasks });
}

function Summary() {
  const ws = useWorkspaceId();
  const wf = useWfContext();
  const { locationName, locationTz, position } = useWfLookups();
  const unread = useUnread();
  const tasks = useMyOpenTasks();
  const notifications = useQuery({ queryKey: qk.notifications(ws ?? ''), queryFn: () => api.notifications(ws!, { limit: 50 }), enabled: Boolean(ws) });
  const openNotifications = useUi(s => s.setNotificationsPanel);
  const upcoming = useShifts();
  const next = upcoming[0];
  const nextWhere = next ? [locationName(next.location_id), position(next.position_id)?.name].filter(Boolean).join(' · ') : '';
  const mentions = unread.reduce((n, c) => n + c.mention_count, 0);
  const day = ukToday();
  const open = tasks.data?.length;
  const overdue = (tasks.data ?? []).filter(t => taskDueOf(t, 'overdue', day)).length;
  const unreadN = notifications.data?.unread_count ?? 0;
  // t369u: the task and shift figures leave with their modules; the rest close up.
  const tasksOn = useModuleOn('tasks');
  const shiftsOn = useModuleOn('shifts');
  const cols = 2 + Number(tasksOn) + Number(shiftsOn);
  return (
    <div className={`grid gap-4 @[16rem]:grid-cols-2 ${cols === 4 ? '@3xl:grid-cols-4' : cols === 3 ? '@3xl:grid-cols-3' : ''}`}>
      <Stat label="Unread conversations"
        value={<StatLink stat="unread" to={chatHref('unread')} label={statLabel(unread.length, 'unread conversation')}>{unread.length}</StatLink>}
        sub={mentions ? <StatLink stat="mentions" to={chatHref('mentions')} label={`View conversations with ${mentions} unread mention${mentions > 1 ? 's' : ''}`}>{mentions} mention{mentions > 1 ? 's' : ''}</StatLink> : 'No mentions'} />
      {tasksOn && <Stat label="My open tasks"
        value={open == null ? '…' : <StatLink stat="tasks-open" to={tasksHref()} label={statLabel(open, 'open task')}>{open}</StatLink>}
        sub={overdue ? <StatLink stat="tasks-overdue" to={tasksHref('overdue')} label={statLabel(overdue, 'overdue task')}>{overdue} overdue</StatLink> : 'Nothing overdue'} />}
      {shiftsOn && <Stat label="Next shift"
        value={next ? <StatLink stat="next-shift" to={myShiftsHref()} label={`View my shifts, next ${shiftWhen(next, locationTz(next.location_id))}`}><span className="block text-lg leading-snug" data-testid="next-shift-when">{shiftWhen(next, locationTz(next.location_id))}</span></StatLink> : '—'}
        sub={next ? <span className="block truncate" title={nextWhere}>{nextWhere}</span> : wf.data?.is_employee ? 'None in the next 2 weeks' : 'Not on the rota'} />}
      <Stat label="Notifications"
        value={<button type="button" onClick={() => openNotifications('unread')} aria-label={statLabel(unreadN, 'unread notification')} data-testid="home-stat" data-stat="notifications"
          className="-m-1 rounded-lg p-1 decoration-2 underline-offset-4 hover:bg-slate-50 hover:underline dark:hover:bg-slate-800/60">{unreadN}</button>}
        sub="unread" />
    </div>
  );
}

function Unread() {
  const nav = useOriginNavigate();
  const { userId } = useAuth();
  const unread = useUnread();
  return (
    <Card title={<T icon={<MessageSquare className={icon} />}>Unread</T>}>
      {!unread.length ? <p className="text-sm text-slate-500">You're all caught up. 🎉</p> : (
        <List>
          {unread.slice(0, 10).map(c => (
            <Row key={c.id} onClick={() => nav(`/c/${c.id}`)}>
              {c.kind === 'channel' ? <Hash className="h-4 w-4 text-slate-500" /> : c.kind === 'room' ? <RoomIcon className="h-4 w-4 text-slate-500" /> : <MessageSquare className="h-4 w-4 text-slate-500" />}
              <span className="flex-1 truncate text-sm font-medium">{conversationName(c, userId)}</span>
              {c.mention_count > 0 && <span className="flex items-center gap-0.5 text-xs font-semibold text-rose-600"><AtSign className="h-3 w-3" />{c.mention_count}</span>}
              <span className="text-xs text-slate-500">{c.unread_count} new</span>
              {c.last_message_at && <span className="text-xs text-slate-500"><RelativeTime iso={c.last_message_at} /></span>}
            </Row>
          ))}
        </List>
      )}
    </Card>
  );
}

function Shifts() {
  const { locationName } = useWfLookups();
  const upcoming = useShifts().slice(0, 5);
  return (
    <Card title={<T icon={<CalendarClock className={icon} />}>Upcoming shifts</T>} actions={<Link to="/workforce/my-shifts" className="text-xs font-medium link">View all</Link>}>
      {!upcoming.length ? <p className="text-sm text-slate-500">No published shifts in the next two weeks.</p> : (
        <ul className="space-y-2">
          {upcoming.map(s => (
            <li key={s.id} className="flex items-center justify-between gap-2 text-sm">
              {/* t338u: the date and times never wrap in a narrow column; the building gives way (truncates) instead. */}
              <span className="shrink-0 whitespace-nowrap font-medium">{dt(s.starts_at).toFormat('ccc d LLL')}</span>
              <ShiftTimesLabel s={s} className="shrink-0 whitespace-nowrap tabular-nums" />
              <span className="min-w-0 flex-1 truncate text-right text-xs text-slate-500">{locationName(s.location_id)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Handovers() {
  return <div className="space-y-2"><PendingHandoversBanner /><Card title={<T icon={<ClipboardCheck className={icon} />}>Handovers</T>} actions={<Link to="/workforce/handovers" className="text-xs font-medium link">Open</Link>}><p className="text-sm text-slate-500">Read the last shift's notes and write yours at the end of your shift.</p></Card></div>;
}

function Checklists() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['home', ws, 'checklists'], queryFn: () => api.ops.myChecklists(ws!), enabled: Boolean(ws) });
  const due = (q.data ?? []).filter(isChecklistDue);
  return (
    <Card title={<T icon={<ClipboardCheck className={icon} />}>Checklists due</T>} actions={<Link to={checklistsDueHref()} className="text-xs font-medium link" aria-label={statLabel(due.length, 'checklist to do', 'checklists to do')} data-testid="home-stat" data-stat="checklists-due">{due.length} to do</Link>}>
      {q.isLoading ? <Spinner /> : !due.length ? <p className="text-sm text-slate-500">Nothing due right now.</p> : (
        <List>
          {due.slice(0, 6).map(r => (
            <Row key={r.id} to={`/checklists/run/${r.id}`}>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.template_name}</span>
              <span className="text-xs text-slate-500">{r.location_name}</span>
              {r.due_by && <span className={`text-xs ${r.due_by < new Date().toISOString() ? 'font-semibold text-rose-600' : 'text-slate-500'}`}>by {clock(dt(r.due_by))}</span>}
            </Row>
          ))}
        </List>
      )}
    </Card>
  );
}

/** Issues as the Issues list shows them by default: open, all buildings you can see (or just yours). */
function useIssueScope(): IssueQuery['scope'] {
  const all = useCanAnywhere('ops.incident.view_all');
  const manage = useCanAnywhere('ops.incident.manage');
  return all || manage ? 'all' : 'mine';
}
function useIssues(q: IssueQuery) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['home', ws, 'incidents', q], queryFn: () => api.ops.incidents(ws!, { scope: q.scope, status: q.status, breached: q.breached || undefined }), enabled: Boolean(ws) });
}
const SEV: Record<string, string> = { low: 'bg-slate-100 text-slate-700', medium: 'bg-amber-50 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200', critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200' };

function Incidents({ size }: { size: WidgetSize }) {
  const scope = useIssueScope();
  const q = useIssues({ scope, status: 'open' });
  const n = q.data?.length ?? 0;
  return (
    <Card title={<T icon={<Wrench className={icon} />}>Open issues</T>} actions={<Link to={issuesHref({ scope, status: 'open' })} className="text-xs font-medium link" aria-label={statLabel(n, 'open issue')} data-testid="home-stat" data-stat="issues-open-list">{q.isLoading ? 'All issues' : `${n} open`}</Link>}>
      {q.isLoading ? <Spinner /> : !q.data?.length ? <p className="text-sm text-slate-500">No open issues at your buildings.</p> : (
        <List>
          {q.data.slice(0, size === 's' ? 4 : 7).map(i => (
            <Row key={i.id} to={`/incidents/${i.id}`}>
              <span className="font-mono text-xs text-slate-500">#{i.ref}</span>
              <span className="min-w-0 flex-1 truncate text-sm">{i.title}</span>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${SEV[i.severity] ?? ''}`}>{sentenceCase(i.severity)}</span>
              {size !== 's' && <span className="w-28 truncate text-right text-xs text-slate-500">{i.location_name}</span>}
            </Row>
          ))}
        </List>
      )}
    </Card>
  );
}

function Onboarding() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['home', ws, 'onboarding'], queryFn: () => api.onboarding.list(ws!, { view: 'mine', status: 'active' }), enabled: Boolean(ws) });
  return (
    <Card title={<T icon={<GraduationCap className={icon} />}>Onboarding</T>} actions={<Link to="/onboarding" className="text-xs font-medium link">Open</Link>}>
      {q.isLoading ? <Spinner /> : !q.data?.length ? <p className="text-sm text-slate-500">Nothing in progress.</p> : (
        <ul className="space-y-3">
          {q.data.slice(0, 4).map(a => (
            <li key={a.id}>
              <Link to={`/onboarding/${a.id}`} className="-mx-1 block rounded-lg px-1 hover:bg-slate-50 dark:hover:bg-slate-800/60" aria-label={`View onboarding for ${a.user_name}: ${a.done} of ${a.total} done${a.overdue ? `, ${a.overdue} overdue` : ''}`} data-testid="home-stat" data-stat="onboarding">
                <div className="flex justify-between text-sm"><PersonButton userId={a.user_id} name={a.user_name} className="rounded font-medium hover:underline">{a.user_name}</PersonButton><span className="text-slate-500">{a.done}/{a.total}</span></div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"><div className="h-full bg-emerald-500" style={{ width: `${a.total ? (a.done / a.total) * 100 : 0}%` }} /></div>
                <div className="mt-0.5 text-xs text-slate-500">{a.template_name}{a.overdue ? <span className="text-rose-600"> · {a.overdue} overdue</span> : null}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** t361u: the desktop side of All actions' training rows: courses assigned to me and not passed yet, soonest due first. */
export function MyTraining() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['training', ws ?? '', 'mine'], queryFn: () => api.training.mine(ws!), enabled: Boolean(ws) });
  const open = (q.data ?? []).filter(a => !a.passed_at);
  const day = ukToday();
  return (
    <Card title={<T icon={<GraduationCap className={icon} />}>My training</T>} actions={<Link to="/training" className="text-xs font-medium link">All</Link>}>
      {q.isLoading ? <Spinner /> : !open.length ? <p className="text-sm text-slate-500">Nothing to complete.</p> : (
        <List>
          {open.slice(0, 6).map(a => {
            const due = trainingDueLabel(a.due_on, day);
            return (
              <Row key={a.id} to={`/training/${a.course_id}`}>
                <span className="min-w-0 flex-1 truncate text-sm" data-testid="home-training">{a.course_title}</span>
                {due && <span className={`shrink-0 text-xs font-medium ${due.overdue ? 'text-rose-700 dark:text-rose-400' : 'text-slate-500'}`} data-testid="home-training-due">{due.text}</span>}
              </Row>
            );
          })}
        </List>
      )}
    </Card>
  );
}

/** t413u: HR changes waiting for my approval. Names open the person sheet; the row opens the Changes to approve list. */
function HrApprovals() {
  const ws = useWorkspaceId();
  const q = useHrApprovals(ws);
  const items = q.data?.items ?? [];
  return (
    <Card title={<T icon={<IdCard className={icon} />}>{hrApprovalsTitle}</T>} actions={<Link to="/settings/people/users?tab=changes" className="text-xs font-medium link">All{q.data && q.data.total > items.length ? ` ${q.data.total}` : ''}</Link>}>
      {q.isLoading ? <Spinner /> : !items.length ? <p className="text-sm text-slate-500">Nothing to approve.</p> : (
        <List>
          {items.map(i => (
            <div key={i.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid="home-hr-approval">
              <PersonButton userId={i.user_id} name={i.user_name} className="shrink-0 font-medium hover:underline">{i.user_name}</PersonButton>
              <Link to="/settings/people/users?tab=changes" className="min-w-0 flex-1 truncate text-slate-600 hover:underline dark:text-slate-300">{i.label}</Link>
              <span className="shrink-0 text-xs text-slate-500">{hrDate(i.requested_at.slice(0, 10))}</span>
            </div>
          ))}
        </List>
      )}
    </Card>
  );
}

function Agenda() {
  const ws = useWorkspaceId();
  const from = today();
  const to = DateTime.fromISO(from, { zone: TZ }).plus({ days: 7 }).toISODate()!;
  const q = useQuery({ queryKey: ['home', ws, 'agenda', from], queryFn: () => api.calendar(ws!, { from, to }), enabled: Boolean(ws) });
  const items = (q.data?.items ?? []).filter(i => i.layer !== 'tasks').sort((a, b) => a.start.localeCompare(b.start)).slice(0, 8);
  return (
    <Card title={<T icon={<CalendarDays className={icon} />}>Agenda</T>} actions={<Link to="/calendar" className="text-xs font-medium link">Calendar</Link>}>
      {q.isLoading ? <Spinner /> : !items.length ? <p className="text-sm text-slate-500">Nothing in the next 7 days.</p> : (
        <ul className="space-y-2">
          {items.map(i => (
            <li key={i.id} className="flex items-start gap-2 text-sm">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: i.color }} />
              <span className="w-24 shrink-0 text-xs text-slate-500">{i.all_day ? DateTime.fromISO(i.start).toFormat('ccc d LLL') : dt(i.start).toFormat('ccc d, HH:mm')}</span>
              <span className="min-w-0 flex-1 truncate">{i.title}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Kudos() {
  const ws = useWorkspaceId();
  const { byId } = useMembers();
  const q = useQuery({ queryKey: ['home', ws, 'kudos'], queryFn: () => api.kudosFeed(ws!, { limit: 4 }), enabled: Boolean(ws) });
  return (
    <Card title={<T icon={<Heart className={icon} />}>Kudos</T>} actions={<Link to="/kudos" className="text-xs font-medium link">Give kudos</Link>}>
      {q.isLoading ? <Spinner /> : !q.data?.kudos.length ? <p className="text-sm text-slate-500">No kudos yet.</p> : (
        <ul className="space-y-3">
          {q.data.kudos.map(k => (
            <li key={k.id} className="text-sm">
              {/* t132u: every person here opens their profile drawer (closing it returns to Home). */}
              <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                {k.recipients.map(r => (
                  <PersonButton key={r.user_id} userId={r.user_id} name={r.display_name} testId="kudos-person"
                    className="group inline-flex cursor-pointer items-center gap-1.5 rounded-md -mx-0.5 px-0.5 font-medium hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900 dark:hover:bg-slate-800 dark:focus-visible:outline-white">
                    <Avatar size={20} name={r.display_name} id={r.user_id} url={byId.get(r.user_id)?.avatar_url} /><span className="group-hover:underline">{r.display_name}</span>
                  </PersonButton>
                ))}
                {!k.recipients.length && <span className="font-medium">{k.teams.map(t => t.name).join(', ')}</span>}
              </div>
              <div className="line-clamp-2 text-slate-600 dark:text-slate-300">{k.body}</div>
              <div className="text-xs text-slate-500">{k.author_id && k.kind === 'kudos' && <>From <PersonButton userId={k.author_id} name={byId.get(k.author_id)?.display_name ?? k.author_name ?? undefined} testId="kudos-person" className="cursor-pointer rounded hover:underline focus-visible:outline focus-visible:outline-2">{byId.get(k.author_id)?.display_name ?? k.author_name ?? 'someone'}</PersonButton> · </>}<RelativeTime iso={k.created_at} /></div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Activity() {
  const ws = useWorkspaceId();
  const openLink = useOpenLink();
  const notifications = useQuery({ queryKey: qk.notifications(ws ?? ''), queryFn: () => api.notifications(ws!, { limit: 50 }), enabled: Boolean(ws) });
  return (
    <Card title={<T icon={<Bell className={icon} />}>Recent activity</T>}>
      <ul className="space-y-3">
        {(notifications.data?.notifications ?? []).slice(0, 6).map(n => (
          <li key={n.id}><button onClick={() => openLink(n.link)} className="text-left"><span className="block text-sm">{n.title}</span><span className="text-xs text-slate-500"><RelativeTime iso={n.created_at} /></span></button></li>
        ))}
        {!notifications.data?.notifications.length && <li className="text-sm text-slate-500">Nothing yet.</li>}
      </ul>
    </Card>
  );
}

function Projects() {
  const projects = useProjects();
  const live = (projects.data?.projects ?? []).filter(p => !p.archived_at);
  return (
    <Card title={<T icon={<Folder className={icon} />}>Projects</T>}>
      {projects.isLoading ? <Spinner /> : !live.length ? <p className="text-sm text-slate-500">No projects</p> : (
      <div className="flex flex-wrap gap-2">
        {live.map(p => (
          <Link key={p.id} to={`/projects/${p.id}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-sm hover:border-accent-line dark:border-slate-700">
            <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />{p.name}
          </Link>
        ))}
      </div>
      )}
    </Card>
  );
}

function KpiHours({ size }: { size: WidgetSize }) {
  const ws = useWorkspaceId();
  const { from, to } = weekRange();
  const q = useQuery({ queryKey: ['home', ws, 'kpi-hours', from], queryFn: () => api.wf.hoursReport(ws!, { from, to }), enabled: Boolean(ws) });
  const t = q.data?.totals;
  const pct = t && t.scheduled_minutes ? Math.round((t.worked_minutes / t.scheduled_minutes) * 100) : null;
  return (
    <Card title={<T icon={<BarChart3 className={icon} />}>Hours this week</T>} actions={<Link to="/workforce/reports" className="text-xs font-medium link">Reports</Link>}>
      {q.isLoading ? <Spinner /> : !t ? <p className="text-sm text-slate-500">No data.</p> : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <StatLink stat="hours-scheduled" to={hoursHref(from, to)} label={`View hours this week: ${minutes(t.scheduled_minutes)} scheduled`}><div className="text-2xl font-semibold tabular-nums">{minutes(t.scheduled_minutes)}</div><div className="text-xs text-slate-500">Scheduled</div></StatLink>
            <StatLink stat="hours-worked" to={hoursHref(from, to)} label={`View hours this week: ${minutes(t.worked_minutes)} worked`}><div className="text-2xl font-semibold tabular-nums">{minutes(t.worked_minutes)}</div><div className="text-xs text-slate-500">Worked</div></StatLink>
            <StatLink stat="hours-approved" to={hoursHref(from, to)} label={`View hours this week: ${minutes(t.approved_minutes)} approved`}><div className="text-2xl font-semibold tabular-nums">{minutes(t.approved_minutes)}</div><div className="text-xs text-slate-500">Approved</div></StatLink>
          </div>
          {pct != null && <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" title={`${pct}% of scheduled hours worked`}><div className="h-full bg-emerald-500" style={{ width: `${Math.min(pct, 100)}%` }} /></div>}
          {/* Same rule as the hours report: Scheduled = shifts with a person, so these rows add up to the figure above; open shifts are shown apart. */}
          {size === 'm' && <ul className="space-y-1 text-sm" data-testid="hours-by-building">{q.data!.by_location.filter(l => l.scheduled_minutes || l.open_minutes).map(l => <li key={l.location_id} className="flex justify-between gap-2"><span className="truncate">{l.name}</span><span className="shrink-0 tabular-nums text-slate-500">{minutes(l.scheduled_minutes)} scheduled</span></li>)}</ul>}
          {t.open_minutes ? <p className="text-xs text-slate-600 dark:text-slate-400" data-testid="hours-open">Plus {minutes(t.open_minutes)} in open shifts, not yet filled (not in Scheduled)</p> : null}
          <p className="text-xs text-slate-500" data-testid="hours-footer">
            {DateTime.fromISO(from).toFormat('d LLL')} – {DateTime.fromISO(to).toFormat('d LLL')}
            {q.data!.scope && q.data!.scope.buildings < q.data!.scope.of && <>{' · '}<span className="underline decoration-dotted underline-offset-2" tabIndex={0} title="Your role lets you see hours for these buildings only. Ask an admin if you need others." data-testid="hours-scope">Showing {q.data!.scope.buildings} of {q.data!.scope.of} buildings</span></>}
          </p>
        </div>
      )}
    </Card>
  );
}

function KpiIncidents() {
  const scope = useIssueScope();
  // Each figure is the Issues list's own query for the filter it links to, so the numbers match the list exactly.
  const openQ: IssueQuery = { scope, status: 'open' };
  const breachedQ: IssueQuery = { scope, status: 'open', breached: true };
  const freshQ: IssueQuery = { scope, status: 'new' };
  const open = useIssues(openQ), breached = useIssues(breachedQ), fresh = useIssues(freshQ);
  const n = (q: typeof open) => q.data?.length ?? 0;
  const cell = (stat: string, q: typeof open, iq: IssueQuery, label: string, one: string, many: string, warn = false) => (
    <StatLink stat={stat} to={issuesHref(iq)} label={statLabel(n(q), one, many)}>
      <div className={`text-2xl font-semibold tabular-nums ${warn && n(q) ? 'text-rose-600' : ''}`}>{n(q)}</div><div className="text-xs text-slate-500">{label}</div>
    </StatLink>
  );
  return (
    <Card title={<T icon={<AlertTriangle className={icon} />}>Issues</T>} actions={<Link to="/incidents" className="text-xs font-medium link">Manage</Link>}>
      {open.isLoading ? <Spinner /> : (
        <div className="grid grid-cols-3 gap-2 text-center">
          {cell('issues-open', open, openQ, 'Open', 'open issue', 'open issues')}
          {cell('issues-breached', breached, breachedQ, 'Past SLA', 'open issue past its SLA', 'open issues past their SLA', true)}
          {cell('issues-new', fresh, freshQ, 'Not yet picked up', 'new issue not yet picked up', 'new issues not yet picked up')}
        </div>
      )}
    </Card>
  );
}

/** Renders one widget by key. */
export function Widget({ k, size }: { k: WidgetKey; size: WidgetSize }) {
  switch (k) {
    case 'summary': return <Summary />;
    case 'announcements': return <HomeAnnouncements />;
    case 'acks': return <AckWidget />;
    case 'tasks': return <MyTasksWidget size={size} />;
    case 'unread': return <Unread />;
    case 'clock': return <ClockWidget />;
    case 'shifts': return <Shifts />;
    case 'handovers': return <Handovers />;
    case 'checklists': return <Checklists />;
    case 'incidents': return <Incidents size={size} />;
    case 'onboarding': return <Onboarding />;
    case 'training': return <MyTraining />;
    case 'hr_approvals': return <HrApprovals />;
    case 'agenda': return <Agenda />;
    case 'birthdays': return <BirthdaysWidget />;
    case 'kudos': return <Kudos />;
    case 'activity': return <Activity />;
    case 'projects': return <Projects />;
    case 'kpi_hours': return <KpiHours size={size} />;
    case 'kpi_incidents': return <KpiIncidents />;
    case 'sales_takings': return <TakingsWidget size={size} />;
    case 'sales_transactions': return <TransactionsWidget size={size} />;
    case 'sales_basket': return <BasketWidget />;
    case 'sales_top': return <TopSellersWidget size={size} />;
    case 'todos': return <MyTasksWidget size={size} />; // retired key (to-dos are private tasks now)
  }
}
